import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import {
  load_ie,
  update_ie,
  load_ie_trainers,
  add_ie_trainer,
  remove_ie_trainer,
} from '../../../utils/ies_services';
import { loadCountriesData, buildCountrySelectOptions } from '../../../utils/countries';
import Modal_proyectos from '../../menus/Modal';
import Team_link from '../crm/modals/Team_link';
import IeLocationMap from './IeLocationMap';
import './Institucion_panel.css';

function getColorForId(id) {
  const hue = (id * 41) % 360;
  return `hsl(${hue}, 60%, 50%)`;
}

function Institucion_panel({ permission }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ieIdParam = searchParams.get('id');
  const ieId = ieIdParam ? parseInt(ieIdParam, 10) : 0;
  const token = localStorage.getItem('token');
  const canWrite = parseInt(permission, 10) === 1;

  const [countryOptions, setCountryOptions] = useState([]);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [zona, setZona] = useState('');
  const [sede, setSede] = useState('');
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(ieId));
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [trainers, setTrainers] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);

  const formatOptionLabel = ({ label, flagUrl }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {flagUrl && (
        <img
          src={flagUrl}
          alt={`Flag of ${label}`}
          style={{ width: '20px', marginRight: '10px' }}
        />
      )}
      <span>{label}</span>
    </div>
  );

  const getOptionByValue = (value) =>
    countryOptions.find((option) => option.value === value) || null;

  const fetchTrainers = async (theIeId) => {
    if (!theIeId) {
      setTrainers([]);
      return;
    }
    const result = await load_ie_trainers(token, theIeId);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    const list = (result?.trainers || []).map((row) => ({
      link_id: row.id,
      name: row.usr_name || row.email_id,
      email: row.email_id,
      user_id: row.user_id,
    }));
    setTrainers(list);
  };

  useEffect(() => {
    loadCountriesData()
      .then((data) => setCountryOptions(buildCountrySelectOptions(data)))
      .catch((error) => console.error('Error fetching countries:', error));
  }, []);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    if (!canWrite && !ieId) {
      navigate('/instituciones', { replace: true });
      return;
    }
    if (!ieId) {
      setIsLoading(false);
      setTrainers([]);
      return;
    }

    async function fetchIe() {
      setIsLoading(true);
      setErrorMsg('');
      const response = await load_ie(token, ieId);
      if (response === 'INVALID') {
        navigate('/');
        return;
      }
      if (!response?.ok) {
        setErrorMsg(response?.error || 'No se pudo cargar la institución.');
        setIsLoading(false);
        return;
      }
      setName(response.name || '');
      setCountry(response.country || '');
      setCity(response.city || '');
      setDepartamento(response.departamento || '');
      setZona(response.zona || '');
      setSede(response.sede || '');
      setLat(response.lat != null ? Number(response.lat) : null);
      setLon(response.lon != null ? Number(response.lon) : null);
      setIsLoading(false);
      fetchTrainers(ieId);
    }

    fetchIe();
  }, [token, ieId, navigate, canWrite]);

  const handleLocationChange = ({ lat: nextLat, lon: nextLon }) => {
    if (!canWrite) return;
    setLat(nextLat);
    setLon(nextLon);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!canWrite) return;
    setErrorMsg('');

    if (!name.trim()) {
      setErrorMsg('El nombre es obligatorio.');
      return;
    }
    if (!country) {
      setErrorMsg('Selecciona un país.');
      return;
    }
    if (!city.trim()) {
      setErrorMsg('La ciudad es obligatoria.');
      return;
    }
    if (!departamento.trim()) {
      setErrorMsg('El departamento es obligatorio.');
      return;
    }
    if (!zona.trim()) {
      setErrorMsg('La zona es obligatoria.');
      return;
    }
    if (!sede.trim()) {
      setErrorMsg('La sede es obligatoria.');
      return;
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      setErrorMsg('Marca la ubicación en el mapa.');
      return;
    }

    setIsSaving(true);
    const response = await update_ie(token, {
      id: ieId || 0,
      name: name.trim(),
      country,
      city: city.trim(),
      departamento: departamento.trim(),
      zona: zona.trim(),
      sede: sede.trim(),
      lat,
      lon,
    });
    setIsSaving(false);

    if (response === 'INVALID') {
      navigate('/');
      return;
    }
    if (!response?.ok) {
      setErrorMsg(response?.error || 'No se pudo guardar.');
      return;
    }

    if (!ieId && response.id) {
      navigate(`/institucion_panel?id=${response.id}`, { replace: true });
    }
  };

  const setNewTrainer = async (email_id) => {
    if (!canWrite || !ieId) return;
    const result = await add_ie_trainer(token, ieId, email_id);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    setModalOpen(false);
    if (!result?.ok) {
      window.alert(result?.error || 'No se pudo agregar el formador.');
      return;
    }
    fetchTrainers(ieId);
  };

  const onRemoveTrainer = async (link_id) => {
    if (!canWrite) return;
    const confirmed = window.confirm('¿Quitar este formador de la institución?');
    if (!confirmed) return;
    const result = await remove_ie_trainer(token, link_id);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result?.ok) {
      window.alert(result?.error || 'No se pudo eliminar.');
      return;
    }
    fetchTrainers(ieId);
  };

  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="container py-4 institucion-panel">
      <Modal_proyectos
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title="Agregar formador"
      >
        <Team_link onSet={(email_id) => setNewTrainer(email_id)} />
      </Modal_proyectos>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <Button
            variant="link"
            className="px-0 mb-1"
            onClick={() => navigate('/instituciones')}
          >
            ← Volver a instituciones
          </Button>
          <h2 className="mb-0">
            {!canWrite ? 'Ver institución' : ieId ? 'Editar institución' : 'Nueva institución'}
          </h2>
        </div>
        {ieId > 0 && (
          <div className="text-muted">
            ID: <strong>{ieId}</strong>
          </div>
        )}
      </div>

      <Form onSubmit={handleSave}>
        <Row className="g-3">
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Nombre de la institución"
                required
                disabled={!canWrite}
                readOnly={!canWrite}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>País</Form.Label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={countryOptions}
                isLoading={countryOptions.length === 0}
                formatOptionLabel={formatOptionLabel}
                onChange={(selectedOption) => {
                  setCountry(selectedOption ? selectedOption.value : '');
                }}
                value={getOptionByValue(country)}
                placeholder="Seleccionar país…"
                isSearchable
                isDisabled={!canWrite}
                styles={{
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ciudad</Form.Label>
              <Form.Control
                type="text"
                maxLength={30}
                value={city}
                onChange={(event) => setCity(event.target.value)}
                placeholder="Ciudad"
                required
                disabled={!canWrite}
                readOnly={!canWrite}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Departamento</Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={departamento}
                onChange={(event) => setDepartamento(event.target.value)}
                placeholder="Departamento"
                required
                disabled={!canWrite}
                readOnly={!canWrite}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Zona</Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={zona}
                onChange={(event) => setZona(event.target.value)}
                placeholder="Zona"
                required
                disabled={!canWrite}
                readOnly={!canWrite}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Sede</Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={sede}
                onChange={(event) => setSede(event.target.value)}
                placeholder="Sede"
                required
                disabled={!canWrite}
                readOnly={!canWrite}
              />
            </Form.Group>

            {ieId > 0 && (
              <div className="mb-3">
                <Form.Label>Formadores</Form.Label>
                <div className="d-flex align-items-center flex-wrap gap-2 ie-trainers">
                  <div className="d-flex flex-wrap gap-2">
                    {trainers.map((s) => {
                      const dynamicColor = getColorForId(
                        parseInt(s.user_id || s.link_id, 10) || 1
                      );
                      return (
                        <span
                          key={s.link_id}
                          className="badge pill chip d-inline-flex align-items-center gap-2 px-3 py-2"
                          style={{ backgroundColor: dynamicColor }}
                          title={s.email}
                        >
                          {s.name && s.name.length > 25
                            ? s.name.slice(0, 25) + '...'
                            : s.name}
                          {canWrite && (
                            <button
                              type="button"
                              aria-label={`Quitar ${s.name}`}
                              className="btn-close btn-close-white btn-close-sm ms-1"
                              onClick={() => onRemoveTrainer(s.link_id)}
                            />
                          )}
                        </span>
                      );
                    })}
                    {trainers.length === 0 && (
                      <span className="text-muted small">Sin formadores</span>
                    )}
                  </div>
                  {canWrite && (
                    <Button
                      variant="outline-dark"
                      size="sm"
                      className="square-btn"
                      type="button"
                      onClick={() => setModalOpen(true)}
                    >
                      +
                    </Button>
                  )}
                </div>
              </div>
            )}

            {errorMsg && <div className="alert alert-danger py-2">{errorMsg}</div>}

            {canWrite && (
              <Button type="submit" variant="primary" disabled={isSaving}>
                {isSaving ? 'Guardando…' : 'Guardar'}
              </Button>
            )}
          </Col>

          <Col xs={12} md={6}>
            <Form.Label>Ubicación</Form.Label>
            <IeLocationMap
              lat={lat}
              lon={lon}
              onChange={handleLocationChange}
              readOnly={!canWrite}
            />
          </Col>
        </Row>
      </Form>
    </div>
  );
}

export default Institucion_panel;
